<script setup lang="ts">
import { ref } from "vue";
import { useFormValidation } from "../../composables/useFormValidation";
import TextInput from "../ui/form/TextInput.vue";
import { email, required } from "../../utils/validation";
import { api } from "../../api/Api";

defineEmits<{
  (e: "back"): void;
}>();


const { fields, formData, setFieldRules, validateForm } =
  useFormValidation({
    email: "",
  });

  setFieldRules('email', [required('Email is required'), email()])

const isLoading = ref(false);
const isSuccess = ref(false);

const handleSubmit = async () => {
  if (!validateForm()) return;

  isLoading.value = true;
  try {
    // Handle password reset logic here
    console.log("Reset password for:", formData.value.email);
    const response = await api.post("/forgot-password", {
      email: formData.value.email,
    })
    if (response.success == true) {
      isSuccess.value = true;
    }
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <div class="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg">
    <div class="text-center">
      <h2 class="text-3xl font-bold text-gray-900 dark:text-white">
        Reset Password
      </h2>
      <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
        Enter your email address and we'll send you instructions to reset your
        password.
      </p>
    </div>

    <div v-if="isSuccess" class="mt-8">
      <div class="rounded-md bg-green-50 dark:bg-green-900 p-4">
        <div class="flex">
          <div class="flex-shrink-0">✓</div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-green-800 dark:text-green-200">
              Check your email
            </h3>
            <div class="mt-2 text-sm text-green-700 dark:text-green-300">
              We've sent you instructions to reset your password.
            </div>
          </div>
        </div>
      </div>

      <button
        @click="$emit('back')"
        class="mt-6 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        Back to Sign In
      </button>
    </div>

    <form
      v-else
      @submit.prevent="handleSubmit"
      class="mt-8 space-y-6"
      novalidate
    >
      <TextInput
        v-model="fields.email.value"
        :error="fields.email.touched ? fields.email.error : null"
        @blur="fields.email.touched = true"
        type="email" label="Email address"
      />

      <div class="flex flex-col space-y-3">
        <button
          type="submit"
          :disabled="isLoading"
          class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span v-if="isLoading">Sending...</span>
          <span v-else>Send Reset Instructions</span>
        </button>

        <button
          type="button"
          @click="$emit('back')"
          class="w-full flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Back to Sign In
        </button>
      </div>
    </form>
  </div>
</template>
